import React, { useState } from 'react'
import axios from 'axios'
import { t } from 'i18n'
import { ButtonContent, searchErrorMessage } from './InheritanceCheckForm'
import Alert from '../utilityComponents/Alert'

const scope = 'components.inheritance_check'

const searchResultText = (searchForm, searchType) => {
  if(searchType === 'personal_code') {
    return t('with_personal_code', { scope, personalCode: searchForm.personalCode })
  }

  const dateOfBirthText = !!searchForm.dateOfBirthTo
    ? t('with_birthdate_interval', {
      scope,
      dateOfBirthFrom: searchForm.dateOfBirthFrom.format(t('date.formats.moment')),
      dateOfBirthTo: searchForm.dateOfBirthTo.format(t('date.formats.moment'))
    })
    : t('with_birthdate', { scope, dateOfBirthFrom: search.dateOfBirthFrom.format(t('date.formats.moment')) })

  const searchText = searchType === 'personal_code'
    ? t('with_personal_code', { scope, personalCode: searchForm.personalCode })
    : t('with_name', { scope, firstName: searchForm.firstName, lastName: searchForm.lastName })

  return `${searchText} ${dateOfBirthText}`
}

const SingleMatterResult = ({
  searchForm, searchType, getMatters, loading, showPaymentDisclaimer, onContinueClick
}) => {
  if(showPaymentDisclaimer) {
    return <PaymentDisclaimer loading={loading} getMatters={getMatters}/>
  }

  return (
    <>
      <Alert type='info'>
        {searchResultText(searchForm, searchType)} {t('one_result_found', { scope })} {t('see_result', { scope })}
      </Alert>
      <div className='text-center'>
        <button type='button'
          className='btn btn-primary btn-lg mt-3 mb-4'
          onClick={onContinueClick}
        >
          <ButtonContent loading={loading} buttonText={t('continue', { scope })}/>
        </button>
      </div>
    </>
  )
}

const PaymentDisclaimer = ({ loading, getMatters }) => {
  return (
    <>
      <Alert type='info'>
        {t('fee_price', { scope })} {t('fee_agree', { scope })}
      </Alert>
      <div className='text-center'>
        <button type='button'
          className='btn btn-primary btn-lg mt-3 mb-4'
          onClick={getMatters}
        >
          <ButtonContent loading={loading} buttonText={t('continue', { scope })}/>
        </button>
      </div>
    </>
  )
}

const testatorFromSearch = (searchForm, testator) => {
  if (testator.surname === searchForm.lastName) {
    return [testator.first_name, testator.other_names].includes(searchForm.firstName)
  }

  return false
}

const TestatorRow = ({ searchForm, matter, onMatterSelect, selected }) => {
  const testator = matter.testators.find(testator => testatorFromSearch(searchForm, testator))
  const otherNames = testator.other_names && `(${testator.other_names})`
  const testatorName = [testator.first_name, otherNames, testator.surname].filter(Boolean).join(' ')
  return (
    <tr key={`${testator.first_name}-${testator.birth_date}`} className='bg-light-blue'>
      <td>
        <input
          type='checkbox'
          className='align-middle'
          name={`select-matter-${matter.matter_id}`}
          aria-label={testatorName}
          checked={selected}
          onChange={() => onMatterSelect(matter.matter_id)}
        />
      </td>
      <td>
        {testatorName}
      </td>
      <td>
        {testator.birth_date}
      </td>
    </tr>
  )
}

const MultipleMattersResult = ({
  searchForm, searchType, getMatters, loading, matterResults, selectedMatters, setSelectedMatters,
  showPaymentDisclaimer, onContinueClick
}) => {

  const onAllSelectedChange = (event) => {
    const checked = event.target.checked
    if(checked) {
      setSelectedMatters(matterResults.map(matter => matter.matter_id))
    } else {
      setSelectedMatters([])
    }
  }

  const onMatterSelect = (matterId) => {
    if(selectedMatters.includes(matterId)) {
      setSelectedMatters(selectedMatters.filter(matter => matter !== matterId))
    } else {
      setSelectedMatters(selectedMatters.concat([matterId]))
    }
  }

  if(showPaymentDisclaimer) {
    return <PaymentDisclaimer loading={loading} getMatters={getMatters}/>
  }

  return(
    <>
      <Alert type='info'>
        {searchResultText(searchForm, searchType)} {t('results_found', { scope, number: matterResults.length })} {t('see_result', { scope })}
      </Alert>
      <table className='table services-table testators-table align-middle'>
        <thead>
          <tr>
            <th className='checkbox-th'>
              <input
                type='checkbox'
                className='align-middle'
                name='select_all'
                aria-label={t('select_all', { scope })}
                checked={selectedMatters.length === matterResults.length}
                onChange={onAllSelectedChange}
              />
            </th>
            <th>{t('table.testators', { scope })}</th>
            <th>{t('table.birth_date', { scope })}</th>
          </tr>
        </thead>
        <tbody>
          {matterResults.map(result =>
            <TestatorRow
              key={result.matter_id}
              matter={result}
              searchForm={searchForm}
              onMatterSelect={onMatterSelect}
              selected={selectedMatters.includes(result.matter_id)}
            />
          )}
        </tbody>
      </table>
      <div className='text-center'>
        <button type='button'
          className='btn btn-primary btn-lg mt-3 mb-4'
          onClick={onContinueClick}
          disabled={!selectedMatters.length}
        >
          <ButtonContent loading={loading} buttonText={t('continue', { scope })}/>
        </button>
      </div>
    </>
  )
}

const getInitialSelectedMatter = (matterResults) => {
  if(matterResults.length === 1) {
    return matterResults.map(matter => matter.matter_id)
  } else {
    return []
  }
}

const SearchResult = ({ matterResults, searchForm, searchType }) => {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [selectedMatters, setSelectedMatters] = useState(getInitialSelectedMatter(matterResults))
  const [showPaymentDisclaimer, setShowPaymentDisclaimer] = useState(false)

  const onContinueClick = () => {
    setLoading(true)
    axios.get('/inheritance_checks/is_free_service').then(({ data }) => {
      if (data.is_free) {
        getMatters()
      } else {
        setShowPaymentDisclaimer(true)
      }
    }).catch(({response}) => {
      setErrors(response.data)
    }).finally(() =>
      setLoading(false)
    )
  }

  const getMatters = () => {
    setLoading(true)
    axios.post('/inheritance_checks/find_matters', {
      inheritance_check: {
        search_type: searchType,
        personal_code: searchForm.personalCode,
        first_name: searchForm.firstName,
        last_name: searchForm.lastName,
        date_of_birth_from: searchForm.dateOfBirthFrom?.format('DD.MM.yyyy'),
        date_of_birth_to: searchForm.dateOfBirthTo?.format('DD.MM.yyyy'),
      },
      matter_ids: selectedMatters
    })
      .then(({ data }) => {
        if(data.inheritanceMatters.length > 0) {
          location = `/inheritance_checks/${data.id}`
        }
      }).catch(({ response }) => {
        setErrors(response.data)
      }).finally(() =>
        setLoading(false)
      )
    }

    return (
      <>
        <Alert>{searchErrorMessage(errors)}</Alert>
        {matterResults.length === 1 &&
          <SingleMatterResult
            searchForm={searchForm}
            searchType={searchType}
            getMatters={getMatters}
            loading={loading}
            matterResults={matterResults}
            showPaymentDisclaimer={showPaymentDisclaimer}
            onContinueClick={onContinueClick}
          />
        }
        {matterResults.length > 1 &&
          <MultipleMattersResult
            searchForm={searchForm}
            searchType={searchType}
            getMatters={getMatters}
            loading={loading}
            matterResults={matterResults}
            selectedMatters={selectedMatters}
            setSelectedMatters={setSelectedMatters}
            showPaymentDisclaimer={showPaymentDisclaimer}
            onContinueClick={onContinueClick}
          />
        }
      </>
    )
  }

export default SearchResult
